/**
 * poseConverter.ts
 *
 * Converts DogPoseDataPoint[] from Redux store into GanttBarData[] for the
 * Behavior Timeline V2 chart.
 *
 * Algorithm:
 * 1. Sort poses by startedAt (or timestamp as fallback)
 * 2. Each pose becomes its own bar (no grouping)
 * 3. For each pose:
 *    - startTime = pose.startedAt (or timestamp)
 *    - endTime = next pose's startTime, or startTime + 30s for the last pose
 *    - type = dogStateToBehaviorType(dogState)
 *    - severity = computed from thresholds
 *    - confidence = bboxConfidence
 */

import type { DogPoseDataPoint } from '../../store/slices/dashboardSlice';
import type { GanttBarData, BehaviorType, Severity, ThresholdConfig, BehaviorThreshold } from './behaviorTypes';
import { dogStateToBehaviorType, DEFAULT_THRESHOLDS } from './behaviorTypes';

// ─── Severity computation ──────────────────────────────────────────────────────

/**
 * Compute severity for a behavior bar based on thresholds.
 *
 * Duration-based behaviors (sitting, standing, lying, rest):
 *   - normal: duration < threshold
 *   - elevated: duration >= threshold (warning)
 *   - alert: duration >= threshold * 1.5 (critical)
 *
 * Frequency-based behaviors (walk, scratch, spin, toilet):
 *   - Use confidence as a proxy.
 *   - normal: confidence >= 0.8
 *   - elevated: confidence >= 0.6
 *   - alert: confidence < 0.6
 */
function computeSeverity(
  behaviorType: BehaviorType,
  durationMs: number,
  confidence: number,
  thresholds: ThresholdConfig,
): Severity {
  const durationMin = durationMs / 60000;

  // Duration-based behaviors — use warning threshold for elevated, critical for alert
  const durationWarningThresholds: Record<string, number | undefined> = {
    sitting: thresholds.sitting.warning?.duration,
    standing: thresholds.standing.warning?.duration,
    lying: thresholds.lying.warning?.duration,
    rest: thresholds.rest.warning?.duration,
  };

  const durationCriticalThresholds: Record<string, number | undefined> = {
    sitting: thresholds.sitting.critical?.duration,
    standing: thresholds.standing.critical?.duration,
    lying: thresholds.lying.critical?.duration,
    rest: thresholds.rest.critical?.duration,
  };

  const warningThreshold = durationWarningThresholds[behaviorType];
  const criticalThreshold = durationCriticalThresholds[behaviorType];
  if (criticalThreshold !== undefined && durationMin >= criticalThreshold) return 'alert';
  if (warningThreshold !== undefined && durationMin >= warningThreshold) return 'elevated';
  if (warningThreshold !== undefined) return 'normal';

  // Frequency-based behaviors — use confidence as proxy for now
  if (confidence >= 0.8) return 'normal';
  if (confidence >= 0.6) return 'elevated';
  return 'alert';
}

// ─── Merge logic ────────────────────────────────────────────────────────────────

/** Default merge gap in milliseconds — same-behavior bars within this gap are merged. */
const DEFAULT_MERGE_GAP_MS = 5000; // 5 seconds

/** Mapping from AlertBehaviorKey (used in ThresholdConfig) to BehaviorType (used in GanttBarData). */
const ALERT_KEY_TO_BEHAVIOR_TYPE: Record<string, BehaviorType> = {
  spin: 'spin',
  scratching: 'scratch',
  walking: 'walk',
  standing: 'standing',
  sitting: 'sitting',
  lying: 'lying',
  rest: 'rest',
  toilet: 'toilet',
};

/** Severity ordering: alert > elevated > normal. Used to take the worst severity when merging. */
function severityLevel(severity: Severity): number {
  switch (severity) {
    case 'alert': return 2;
    case 'elevated': return 1;
    case 'normal': return 0;
  }
}

/** Return the worse of two severities. */
function worstSeverity(a: Severity, b: Severity): Severity {
  return severityLevel(a) >= severityLevel(b) ? a : b;
}

/**
 * Merge same-type bars that are close together, even if there are different-type
 * bars in between. This handles rapid alternation like:
 *   walk(10s) → scratch(0.2s) → walk(7s) → walk(17s merged)
 *
 * Each behavior type can have its own merge gap (in ms). If a behavior is not
 * in the map, it falls back to defaultMergeGapMs.
 *
 * Merge strategy:
 * - Duration: spans from first bar's start to last bar's end (includes gaps)
 * - Severity: worst (highest) severity across all merged segments
 * - Confidence: duration-weighted average (by detection duration, excluding gaps)
 * - poseCount: sum of all merged pose counts
 */
function mergeBars(
  bars: GanttBarData[],
  defaultMergeGapMs: number = DEFAULT_MERGE_GAP_MS,
  perBehaviorMergeGapMs?: Partial<Record<BehaviorType, number>>,
): GanttBarData[] {
  if (bars.length === 0) return [];

  const merged: GanttBarData[] = [];
  const consumed = new Set<number>();

  for (let i = 0; i < bars.length; i++) {
    if (consumed.has(i)) continue;

    const current = { ...bars[i] };
    let currentPoseCount = current.poseCount ?? 1;
    // Track confidence weighting by detection duration (excluding gaps)
    let totalConfidenceWeight = current.confidence * (current.endTime - current.startTime);
    let totalDetectionDuration = current.endTime - current.startTime;

    // Look ahead for same-type bars within the behavior's merge gap, skipping different-type bars
    const behaviorMergeGap = perBehaviorMergeGapMs?.[current.type] ?? defaultMergeGapMs;
    for (let j = i + 1; j < bars.length; j++) {
      if (consumed.has(j)) continue;
      const next = bars[j];
      const gap = next.startTime - current.endTime;

      // If gap exceeds this behavior's merge threshold, stop looking (bars are sorted by startTime)
      if (gap > behaviorMergeGap) break;

      // If same type and within gap, merge
      if (current.type === next.type) {
        const nextDur = next.endTime - next.startTime;
        totalConfidenceWeight += next.confidence * nextDur;
        totalDetectionDuration += nextDur;
        current.endTime = next.endTime;
        current.severity = worstSeverity(current.severity, next.severity);
        currentPoseCount += next.poseCount ?? 1;
        current.poseCount = currentPoseCount;
        consumed.add(j);
        // Continue looking — extending current.endTime might bring more bars within range
      }
      // If different type, skip and continue looking for same-type bars
    }

    // Compute final confidence as weighted average by detection duration (excluding gaps)
    current.confidence = totalDetectionDuration > 0
      ? Math.round(totalConfidenceWeight / totalDetectionDuration * 100) / 100
      : current.confidence;
    merged.push(current);
  }

  return merged;
}

// ─── Main conversion function ──────────────────────────────────────────────────

/** Minimum bar duration in ms — ensures very short poses are still visible */
const MIN_BAR_DURATION_MS = 1000; // 1 seconds

/** Default duration for the last pose when no next pose exists */
const DEFAULT_LAST_POSE_DURATION_MS = 30000; // 30 seconds

/**
 * Convert an array of DogPoseDataPoint from Redux into GanttBarData[] for
 * the Behavior Timeline V2 chart.
 *
 * Each pose becomes its own bar — no grouping/merging.
 * The endTime of each pose is the startTime of the next pose,
 * or startTime + 30s for the last pose.
 *
 * @param poses - Array of DogPoseDataPoint from Redux store
 * @param thresholds - Optional threshold configuration for severity computation
 * @returns Array of GanttBarData suitable for the timeline chart
 */
export function convertPosesToGanttBars(
  poses: DogPoseDataPoint[],
  thresholds: ThresholdConfig = DEFAULT_THRESHOLDS,
  mergeEnabled: boolean = true,
  defaultMergeGapMs: number = DEFAULT_MERGE_GAP_MS,
): GanttBarData[] {
  if (!poses || poses.length === 0) return [];

  // Sort by startedAt (or timestamp as fallback)
  const sorted = [...poses].sort((a, b) => {
    const aTime = a.startedAt ?? a.timestamp;
    const bTime = b.startedAt ?? b.timestamp;
    return aTime - bTime;
  });

  const now = Date.now();
  const bars: GanttBarData[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const pose = sorted[i];
    const dogState = pose.dogState ?? 'unknown';
    const startTime = pose.startedAt ?? pose.timestamp;
    const confidence = pose.bboxConfidence ?? 0;

    // Skip poses with invalid timestamps (future timestamps beyond 1 minute)
    if (startTime > now + 60000) continue;

    // endTime = next pose's startTime, or startTime + default duration for last pose
    let endTime: number;
    if (i < sorted.length - 1) {
      const nextPose = sorted[i + 1];
      endTime = nextPose.startedAt ?? nextPose.timestamp;
    } else {
      endTime = startTime + DEFAULT_LAST_POSE_DURATION_MS;
    }

    // Ensure minimum bar duration so short poses are still visible
    if (endTime - startTime < MIN_BAR_DURATION_MS) {
      endTime = startTime + MIN_BAR_DURATION_MS;
    }

    const behaviorType = dogStateToBehaviorType(dogState);
    const durationMs = endTime - startTime;

    bars.push({
      id: `pose-${dogState}-${startTime}-${i}`,
      type: behaviorType,
      startTime,
      endTime,
      severity: computeSeverity(behaviorType, durationMs, confidence, thresholds),
      confidence: Math.round(confidence * 100) / 100,
      poseCount: 1,
    });
  }

  // Build per-behavior merge gap map from thresholds (convert seconds to ms)
  const perBehaviorMergeGapMs: Partial<Record<BehaviorType, number>> = {};
  for (const [key, cfg] of Object.entries(thresholds) as [string, BehaviorThreshold | number][]) {
    if (typeof cfg === 'object' && cfg.mergeGapSec != null) {
      const behaviorType = ALERT_KEY_TO_BEHAVIOR_TYPE[key];
      if (behaviorType) {
        perBehaviorMergeGapMs[behaviorType] = cfg.mergeGapSec * 1000;
      }
    }
  }

  // Merge same-type bars within their merge gap (skip in detail mode)
  const finalBars = mergeEnabled ? mergeBars(bars, defaultMergeGapMs, perBehaviorMergeGapMs) : bars;

  console.log(`[poseConverter] Input: ${poses.length} poses → ${bars.length} bars${mergeEnabled ? ` → ${finalBars.length} merged bars` : ' (detail mode, no merge)'}`,
    finalBars.map(b => `${b.type}(${((b.endTime - b.startTime) / 1000).toFixed(1)}s)`).join(', '));

  return finalBars;
}

/**
 * Get the current (most recent) behavior type from poses.
 * Useful for the PostureMonitor component.
 */
export function getCurrentBehavior(poses: DogPoseDataPoint[]): {
  type: BehaviorType;
  confidence: number;
} | null {
  if (!poses || poses.length === 0) return null;

  // Sort by timestamp descending to get the most recent
  const sorted = [...poses].sort((a, b) => b.timestamp - a.timestamp);
  const latest = sorted[0];

  return {
    type: dogStateToBehaviorType(latest.dogState ?? 'unknown'),
    confidence: latest.bboxConfidence ?? 0,
  };
}

/**
 * Compute behavior statistics from GanttBarData[].
 * Returns counts and durations for each behavior type.
 */
export function computeBehaviorStats(bars: GanttBarData[]): {
  counts: Record<BehaviorType, number>;
  durations: Record<BehaviorType, number>; // in minutes
  totalDuration: number; // in minutes
} {
  const counts: Record<string, number> = {};
  const durations: Record<string, number> = {};

  for (const bar of bars) {
    counts[bar.type] = (counts[bar.type] ?? 0) + 1;
    const durationMin = (bar.endTime - bar.startTime) / 60000;
    durations[bar.type] = (durations[bar.type] ?? 0) + durationMin;
  }

  const totalDuration = Object.values(durations).reduce((sum, d) => sum + d, 0);

  return {
    counts: counts as Record<BehaviorType, number>,
    durations: durations as Record<BehaviorType, number>,
    totalDuration,
  };
}